import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Hint from '../components/common/Hint'
import {
  Form,
  Input,
  Switch,
  Select,
  InputNumber,
  Button,
  Card,
  Divider,
  Space,
  message,
  Tag,
  Popconfirm,
  Upload
} from 'antd'
import {
  ArrowLeftOutlined,
  CloseOutlined,
  UploadOutlined,
  DeleteOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import useGoBack from '../hooks/useGoBack'
import {
  createRevisitReminderConfig,
  updateRevisitReminderConfig,
  getRevisitReminderConfig,
  getCustomerTags,
  getMembershipTiers,
  getProducts,
  uploadRevisitReminderImage
} from '../api/revisitReminder'
import { PHOTO_URL } from '../utils/apiutils'
import { Spin } from 'antd'

const { TextArea } = Input
const { Option } = Select

// 附圖存的是 B2 相對路徑，畫面顯示時要補上圖床網址
const resolveImageUrl = (url) =>
  !url ? '' : /^https?:\/\//.test(url) ? url : `${PHOTO_URL}${url}`

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`

const FormCard = styled(Card)`
  margin-top: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`

const VariableButton = styled(Button)`
  margin: 4px;
`

const PresetContentCard = styled(Card)`
  margin-bottom: 16px;
  background: #f9f9f9;

  .ant-card-body {
    padding: 12px;
  }
`

const PresetContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`

const PresetContentText = styled.div`
  color: #666;
  font-size: 14px;
  line-height: 1.6;
`

const RevisitReminderConfigPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const goBack = useGoBack('/customers/revisit-reminders')
  const { user } = useAuth()
  const { permissionsLoaded, isBrandPlan } = useMembership()
  const [form] = Form.useForm()
  // 保留載入時的 autoSend，供存檔時判斷是否為「新開啟」以決定 enabledAt（不回填基準）
  const loadedAutoSendRef = useRef({
    enabled: false,
    enabledAt: null,
    lastAutoRunAt: null
  })
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(!!id)
  const [customerTags, setCustomerTags] = useState([])
  const [membershipTiers, setMembershipTiers] = useState([])
  const [products, setProducts] = useState([])
  // 附圖：LINE 會另外送一則獨立圖片訊息，Email 直接內嵌
  const [reminderImage, setReminderImage] = useState({
    imageUrl: '',
    imagePreviewUrl: ''
  })
  const [uploadingImage, setUploadingImage] = useState(false)

  const businessId = user?.businessId
  const isEditMode = !!id

  // 变量映射：英文变量名 -> 中文显示文本
  const variableLabels = {
    '{customerName}': '客戶姓名',
    '{businessName}': '商家名稱',
    '{serviceName}': '服務名稱',
    '{lastVisitDate}': '最後訪問日期'
  }

  // 所有 Hooks 必须在早期返回之前调用
  // 使用 Form.useWatch 监听表单字段变化，实时显示预设内容
  const titleValue = Form.useWatch('message.title', form) || ''
  const contentValue = Form.useWatch('message.content', form) || ''
  const lineMessageValue = Form.useWatch('message.lineMessage', form) || ''
  const emailContentValue = Form.useWatch('message.emailContent', form) || ''
  // 關掉按鈕時，「按鈕文字」就沒有意義，整欄收起來
  const showBookingButton =
    Form.useWatch('message.showBookingButton', form) !== false
  const timingType = Form.useWatch('timing.type', form)
  const isBroadcast = timingType === 'broadcast'

  // 计算当前预设内容（从表单字段获取）
  const currentPresetContents = {
    title: titleValue,
    content: contentValue,
    lineMessage: lineMessageValue,
    emailContent: emailContentValue
  }

  // 获取所有服务类别
  const categories = [
    ...new Set(products.map((p) => p.category).filter(Boolean))
  ]

  // 检查是否有任何预设内容
  const hasPresetContent = Object.values(currentPresetContents).some((v) => v)

  useEffect(() => {
    if (businessId) {
      fetchCustomerTags()
      fetchMembershipTiers()
      fetchProducts()
      if (isEditMode) {
        fetchConfig()
      } else {
        // 新建模式，设置默认值
        form.setFieldsValue({
          isEnabled: true,
          'autoSend.enabled': false,
          'timing.type': 'revisit',
          'timing.daysSinceLastVisit': 14,
          'timing.daysAfterCompletion': 30,
          'timing.reminderInterval': 30,
          'timing.maxReminders': 2,
          'timing.maxDaysSinceLastVisit': null,
          'timing.skipIfHasUpcomingBooking': false,
          'message.showBookingButton': true,
          'message.buttonLabel': '',
          'sending.batchSize': 50,
          'sending.priority': ['line', 'email']
        })
      }
    }
  }, [businessId, isEditMode])

  const fetchConfig = async () => {
    if (!businessId || !id) return
    setFetching(true)
    try {
      const response = await getRevisitReminderConfig(businessId, id)
      if (response.data.success) {
        const config = response.data.data
        loadedAutoSendRef.current = {
          enabled: config.autoSend?.enabled || false,
          enabledAt: config.autoSend?.enabledAt || null,
          lastAutoRunAt: config.autoSend?.lastAutoRunAt || null
        }
        form.setFieldsValue({
          name: config.name,
          isEnabled: config.isEnabled,
          'autoSend.enabled': config.autoSend?.enabled || false,
          'filters.tags': config.filters?.tags || [],
          'filters.membershipLevels': config.filters?.membershipLevels || [],
          'filters.includeBirthdayCustomers':
            config.filters?.includeBirthdayCustomers || false,
          'filters.includeAllCustomers':
            config.filters?.includeAllCustomers || false,
          'filters.minTotalSpent': config.filters?.minTotalSpent,
          'filters.minBookingCount': config.filters?.minBookingCount,
          'timing.type': config.timing?.type || 'revisit',
          'timing.daysSinceLastVisit': config.timing?.daysSinceLastVisit || 14,
          'timing.daysAfterCompletion':
            config.timing?.daysAfterCompletion || 30,
          'timing.reminderInterval': config.timing?.reminderInterval || 30,
          // 0 是合法值（不限），不能用 || 判斷
          'timing.maxReminders':
            config.timing?.maxReminders ?? 2,
          // 0 = 不限，表單上顯示成空白
          'timing.maxDaysSinceLastVisit':
            config.timing?.maxDaysSinceLastVisit || null,
          'timing.skipIfHasUpcomingBooking':
            config.timing?.skipIfHasUpcomingBooking === true,
          'serviceFilters.serviceIds': config.serviceFilters?.serviceIds || [],
          'serviceFilters.categories': config.serviceFilters?.categories || [],
          'message.title': config.message?.title || '',
          'message.content': config.message?.content || '',
          'message.lineMessage': config.message?.lineMessage || '',
          'message.emailContent': config.message?.emailContent || '',
          // 舊設定沒有這個欄位，視為顯示按鈕（維持原本行為）
          'message.showBookingButton':
            config.message?.showBookingButton !== false,
          'message.buttonLabel': config.message?.buttonLabel || '',
          'sending.batchSize': config.sending?.batchSize || 50,
          'sending.priority': config.sending?.priority || ['line', 'email']
        })
        setReminderImage({
          imageUrl: config.message?.imageUrl || '',
          imagePreviewUrl: config.message?.imagePreviewUrl || ''
        })
      }
    } catch (error) {
      console.error('獲取配置失敗:', error)
      message.error('獲取配置失敗')
    } finally {
      setFetching(false)
    }
  }

  const fetchCustomerTags = async () => {
    if (!businessId) return
    try {
      const response = await getCustomerTags(businessId)
      if (response.data.success) {
        setCustomerTags(response.data.data || [])
      }
    } catch (error) {
      console.error('獲取客戶標籤失敗:', error)
    }
  }

  const fetchMembershipTiers = async () => {
    if (!businessId) return
    try {
      const response = await getMembershipTiers(businessId)
      if (response.data.success) {
        const tiers = response.data.data?.tiers || []
        setMembershipTiers(tiers)
      }
    } catch (error) {
      console.error('獲取會員等級失敗:', error)
    }
  }

  const fetchProducts = async () => {
    if (!businessId) return
    try {
      const response = await getProducts(businessId)
      setProducts(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      console.error('獲取服務列表失敗:', error)
    }
  }

  // 插入变量到输入框
  const insertVariable = (fieldName, variable) => {
    const currentValue = form.getFieldValue(fieldName) || ''
    // 获取当前活动的输入框
    const activeElement = document.activeElement
    let input = null

    // 如果是输入框或文本域，使用它
    if (
      activeElement &&
      (activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA')
    ) {
      input = activeElement
    } else {
      // 否则查找对应的输入框
      const fieldSelector = fieldName.replace(/\./g, '\\.')
      input = document.querySelector(`[name="${fieldSelector}"]`)
    }

    if (input && (input.tagName === 'INPUT' || input.tagName === 'TEXTAREA')) {
      const start = input.selectionStart || currentValue.length
      const end = input.selectionEnd || currentValue.length
      const newValue =
        currentValue.substring(0, start) +
        variable +
        currentValue.substring(end)

      form.setFieldsValue({ [fieldName]: newValue })

      // 设置光标位置
      setTimeout(() => {
        input.focus()
        const newCursorPos = start + variable.length
        if (input.setSelectionRange) {
          input.setSelectionRange(newCursorPos, newCursorPos)
        }
      }, 0)
    } else {
      // 如果找不到输入框，就追加到末尾
      const newValue = currentValue + variable
      form.setFieldsValue({ [fieldName]: newValue })
    }
  }

  // 清除预设内容
  const clearPresetContent = (field) => {
    form.setFieldsValue({ [`message.${field}`]: '' })
  }

  // 清除所有预设内容
  const clearAllPresetContents = () => {
    form.setFieldsValue({
      'message.title': '',
      'message.content': '',
      'message.lineMessage': '',
      'message.emailContent': ''
    })
  }

  // 回傳 Upload.LIST_IGNORE 擋掉 antd 內建上傳，改走自己的 API
  const handleImageUpload = async (file) => {
    if (!(file.type || '').startsWith('image/')) {
      message.error('只能上傳圖片檔')
      return Upload.LIST_IGNORE
    }
    if (file.size > 10 * 1024 * 1024) {
      message.error('圖片不能超過 10MB')
      return Upload.LIST_IGNORE
    }

    setUploadingImage(true)
    try {
      const response = await uploadRevisitReminderImage(businessId, file)
      const { imageUrl, imagePreviewUrl } = response.data || {}
      if (!imageUrl) {
        throw new Error('伺服器未回傳圖片網址')
      }
      setReminderImage({
        imageUrl,
        imagePreviewUrl: imagePreviewUrl || ''
      })
      message.success('圖片已上傳，記得按下方「更新」才會存檔')
    } catch (error) {
      console.error('上傳附圖失敗:', error)
      message.error(error.response?.data?.message || '圖片上傳失敗')
    } finally {
      setUploadingImage(false)
    }
    return Upload.LIST_IGNORE
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      // 自動發送：新開啟時才蓋 enabledAt（不回填基準）；已開啟則沿用原 enabledAt
      const prevAuto = loadedAutoSendRef.current || {
        enabled: false,
        enabledAt: null,
        lastAutoRunAt: null
      }
      const autoEnabled = values['autoSend.enabled'] || false
      let autoEnabledAt = prevAuto.enabledAt || null
      if (autoEnabled && !prevAuto.enabled) {
        autoEnabledAt = new Date().toISOString()
      }

      const configData = {
        name: values.name,
        isEnabled: values.isEnabled,
        autoSend: {
          enabled: autoEnabled,
          enabledAt: autoEnabledAt,
          lastAutoRunAt: prevAuto.lastAutoRunAt || null
        },
        filters: {
          tags: values['filters.tags'] || [],
          membershipLevels: values['filters.membershipLevels'] || [],
          includeBirthdayCustomers:
            values['filters.includeBirthdayCustomers'] || false,
          includeAllCustomers: values['filters.includeAllCustomers'] || false,
          minTotalSpent: values['filters.minTotalSpent'] || 0,
          minBookingCount: values['filters.minBookingCount'] || 0
        },
        timing: {
          type: values['timing.type'],
          daysSinceLastVisit: values['timing.daysSinceLastVisit'] || 14,
          daysAfterCompletion: values['timing.daysAfterCompletion'] || 30,
          reminderInterval: values['timing.reminderInterval'] || 30,
          // 留空 = 0 = 不限
          maxDaysSinceLastVisit:
            Number(values['timing.maxDaysSinceLastVisit']) || 0,
          skipIfHasUpcomingBooking:
            values['timing.skipIfHasUpcomingBooking'] === true,
          /* 公告類型留空要當成「每人一次」，不能落進 || 0 變成不限 —— 
             那會讓店家每按一次發送，全部會員就再收一封。 */
          maxReminders:
            values['timing.type'] === 'broadcast'
              ? values['timing.maxReminders'] === undefined ||
                values['timing.maxReminders'] === null ||
                values['timing.maxReminders'] === ''
                ? 1
                : Number(values['timing.maxReminders'])
              : Number(values['timing.maxReminders']) || 0
        },
        serviceFilters: {
          serviceIds: values['serviceFilters.serviceIds'] || [],
          categories: values['serviceFilters.categories'] || []
        },
        message: {
          title: values['message.title'],
          content: values['message.content'],
          lineMessage: values['message.lineMessage'] || null,
          emailContent: values['message.emailContent'] || null,
          showBookingButton: values['message.showBookingButton'] !== false,
          buttonLabel: values['message.buttonLabel'] || null,
          imageUrl: reminderImage.imageUrl || null,
          imagePreviewUrl: reminderImage.imagePreviewUrl || null
        },
        sending: {
          batchSize: values['sending.batchSize'] || 50,
          priority: values['sending.priority'] || ['line', 'email']
        }
      }

      setLoading(true)
      let response
      if (isEditMode) {
        response = await updateRevisitReminderConfig(businessId, id, configData)
      } else {
        response = await createRevisitReminderConfig(businessId, configData)
      }

      if (response.data.success) {
        message.success(isEditMode ? '更新成功' : '創建成功')
        navigate('/customers/revisit-reminders')
      } else {
        message.error(response.data.message || '操作失敗')
      }
    } catch (error) {
      console.error('提交失敗:', error)
      if (error.errorFields) {
        return
      }
      message.error(error.response?.data?.message || '操作失敗')
    } finally {
      setLoading(false)
    }
  }

  // 权限检查 - 在 return 中处理，而不是早期返回
  if (!permissionsLoaded) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size='large' tip='正在載入權限...' />
      </div>
    )
  }

  if (!isBrandPlan()) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Card>
          <h2>功能限制</h2>
          <p>此功能僅限品牌方案用戶使用，請升級您的會員方案。</p>
        </Card>
      </div>
    )
  }

  if (fetching) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size='large' tip='正在載入配置...' />
      </div>
    )
  }

  return (
    <PageContainer>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={goBack}
        style={{ marginBottom: '16px' }}
      >
        返回
      </Button>

      <FormCard>
        <h2>{isEditMode ? '編輯回訪提醒配置' : '新增回訪提醒配置'}</h2>

        {/* 预设内容显示 */}
        {hasPresetContent && (
          <PresetContentCard>
            <PresetContentHeader>
              <strong>預設內容</strong>
              <Button
                type='link'
                danger
                size='small'
                icon={<CloseOutlined />}
                onClick={clearAllPresetContents}
              >
                清除全部
              </Button>
            </PresetContentHeader>
            {currentPresetContents.title && (
              <PresetContentText style={{ marginBottom: '8px' }}>
                <strong>標題：</strong>
                {currentPresetContents.title}
                <Button
                  type='link'
                  danger
                  size='small'
                  icon={<CloseOutlined />}
                  onClick={() => clearPresetContent('title')}
                  style={{ marginLeft: '8px' }}
                >
                  刪除
                </Button>
              </PresetContentText>
            )}
            {currentPresetContents.content && (
              <PresetContentText style={{ marginBottom: '8px' }}>
                <strong>內容：</strong>
                {currentPresetContents.content}
                <Button
                  type='link'
                  danger
                  size='small'
                  icon={<CloseOutlined />}
                  onClick={() => clearPresetContent('content')}
                  style={{ marginLeft: '8px' }}
                >
                  刪除
                </Button>
              </PresetContentText>
            )}
            {currentPresetContents.lineMessage && (
              <PresetContentText style={{ marginBottom: '8px' }}>
                <strong>LINE 訊息：</strong>
                {currentPresetContents.lineMessage}
                <Button
                  type='link'
                  danger
                  size='small'
                  icon={<CloseOutlined />}
                  onClick={() => clearPresetContent('lineMessage')}
                  style={{ marginLeft: '8px' }}
                >
                  刪除
                </Button>
              </PresetContentText>
            )}
            {currentPresetContents.emailContent && (
              <PresetContentText>
                <strong>Email 內容：</strong>
                {currentPresetContents.emailContent}
                <Button
                  type='link'
                  danger
                  size='small'
                  icon={<CloseOutlined />}
                  onClick={() => clearPresetContent('emailContent')}
                  style={{ marginLeft: '8px' }}
                >
                  刪除
                </Button>
              </PresetContentText>
            )}
          </PresetContentCard>
        )}

        <Form form={form} layout='vertical' onFinish={handleSubmit}>
          {/* 基本資訊 */}
          <Divider orientation='left'>基本資訊</Divider>
          <Form.Item
            name='name'
            label='配置名稱'
            rules={[{ required: true, message: '請輸入配置名稱' }]}
          >
            <Input placeholder='請輸入配置名稱' />
          </Form.Item>

          <Form.Item name='isEnabled' label='是否啟用' valuePropName='checked'>
            <Switch />
          </Form.Item>

          {/* 公告是「我現在要發這一則」，掛排程只會變成每有新客人升等就自動補發一封 */}
          {!isBroadcast && (
            <Form.Item
              name='autoSend.enabled'
              label='自動發送'
              valuePropName='checked'
              extra='開啟後系統會每天 09:00 依此設定自動發送，不需手動按發送；只發啟用後新符合的客人（不回填歷史）。仍可隨時手動立即發送。'
            >
              <Switch checkedChildren='自動' unCheckedChildren='手動' />
            </Form.Item>
          )}

          {/* 篩選條件 */}
          <Divider orientation='left'>篩選條件</Divider>
          <Form.Item name='filters.tags' label='會員標籤'>
            <Select mode='multiple' placeholder='請選擇會員標籤' allowClear>
              {customerTags.map((tag) => (
                <Option key={tag} value={tag}>
                  {tag}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name='filters.membershipLevels' label='會員等級'>
            <Select mode='multiple' placeholder='請選擇會員等級' allowClear>
              {membershipTiers.map((tier) => (
                <Option key={tier._id} value={tier.name}>
                  {tier.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name='filters.includeBirthdayCustomers'
            label='當月生日客人'
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name='filters.includeAllCustomers'
            label='包含所有客戶'
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>

          <Form.Item name='filters.minTotalSpent' label='最小消費金額'>
            <InputNumber
              min={0}
              placeholder='請輸入最小消費金額'
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item name='filters.minBookingCount' label='最小預約次數'>
            <InputNumber
              min={0}
              placeholder='請輸入最小預約次數'
              style={{ width: '100%' }}
            />
          </Form.Item>

          {/* 提醒時機 */}
          <Divider orientation='left'>提醒時機</Divider>
          <Form.Item
            name='timing.type'
            label='提醒類型'
            rules={[{ required: true, message: '請選擇提醒類型' }]}
          >
            <Select>
              <Option value='revisit'>再回訪提醒（很久沒來的客人）</Option>
              <Option value='followup'>
                預約結束後貼心提醒（做完療程的關心）
              </Option>
              <Option value='broadcast'>
                節慶／公告通知（直接發給某個會員等級或標籤）
              </Option>
            </Select>
          </Form.Item>

          {isBroadcast && (
            <Hint tone='info' title='節慶／公告通知跟「多久沒來」無關'>
              名單直接由上面的「篩選條件」決定（會員等級、標籤…），不看客人幾時來過，
              所以今天剛到店的客人、甚至還沒有任何已完成預約的客人都發得到。
              中秋禮、節慶祝賀、會員公告請用這一種。
              這個類型只能手動發送，預設每位客人只會收到一次。
            </Hint>
          )}

          {timingType === 'revisit' && (
            <Form.Item
              name='timing.daysSinceLastVisit'
              label='距離上次完成預約天數'
              rules={[{ required: true, message: '請輸入天數' }]}
            >
              <InputNumber
                min={1}
                placeholder='請輸入天數'
                style={{ width: '100%' }}
              />
            </Form.Item>
          )}

          {timingType === 'revisit' && (
            <Form.Item
              name='timing.maxDaysSinceLastVisit'
              label='超過幾天沒來就不再提醒'
              extra='留空 = 不限。不設上限的話，第一次開啟時所有很久以前來過的客人（包含一年以上沒來的）都會一次收到。例如上面填 10、這裡填 30：只提醒上次到店在 10～30 天前的客人。'
              dependencies={['timing.daysSinceLastVisit']}
              rules={[
                ({ getFieldValue }) => ({
                  validator (_, value) {
                    const min = Number(getFieldValue('timing.daysSinceLastVisit')) || 0
                    if (!value || Number(value) > min) return Promise.resolve()
                    return Promise.reject(
                      new Error('要大於「距離上次完成預約天數」')
                    )
                  }
                })
              ]}
            >
              <InputNumber
                min={1}
                placeholder='不限'
                style={{ width: '100%' }}
              />
            </Form.Item>
          )}

          {timingType === 'followup' && (
            <Form.Item
              name='timing.daysAfterCompletion'
              label='預約結束後多少天發送'
              rules={[{ required: true, message: '請輸入天數' }]}
            >
              <InputNumber
                min={1}
                placeholder='請輸入天數'
                style={{ width: '100%' }}
              />
            </Form.Item>
          )}

          {timingType === 'followup' && (
            <Form.Item
              name='timing.skipIfHasUpcomingBooking'
              label='已約好下一次的客人不發'
              valuePropName='checked'
              extra='開啟後，發送當天已經有「今天以後」預約的客人就不會收到（有設定下方服務項目時，只看同樣服務的預約）。適合「做完第 N 天提醒回來保養」；術後關心這類不管有沒有下次預約都該發的，請保持關閉。'
            >
              <Switch />
            </Form.Item>
          )}

          {!isBroadcast && (
            <Form.Item
              name='timing.reminderInterval'
              label='兩次提醒的最小間隔天數'
              rules={[{ required: true, message: '請輸入天數' }]}
              extra='同一位客人被這個設定提醒過之後，要隔多久才會再提醒一次。'
            >
              <InputNumber
                min={1}
                placeholder='請輸入天數'
                style={{ width: '100%' }}
              />
            </Form.Item>
          )}

          <Form.Item
            name='timing.maxReminders'
            label={isBroadcast ? '每位客人最多收到幾次' : '最多提醒幾次'}
            extra={
              isBroadcast
                ? '預設 1 = 每位客人只會收到這則通知一次，之後再按發送也只會補發給還沒收到的人（含這個功能上線前就發過的紀錄）。填 0 = 不限，每次發送所有符合條件的人都會再收到一次。'
                : '同一位客人最多提醒幾次，發完就停。填 1 = 只提醒一次；留空或填 0 = 不限次數（客人一直沒回來就會依上面的間隔一直提醒）。生日提醒不受此限，系統一律一年只祝一次。'
            }
          >
            <InputNumber
              min={0}
              placeholder={isBroadcast ? '預設 1 次' : '預設 2 次'}
              style={{ width: '100%' }}
            />
          </Form.Item>

          {/* 服務項目篩選：公告發的是「哪些會員」，跟做過哪個療程無關 */}
          {!isBroadcast && (
          <>
          <Divider orientation='left'>服務項目篩選</Divider>
          <Form.Item name='serviceFilters.serviceIds' label='針對特定服務'>
            <Select
              mode='multiple'
              placeholder='請選擇服務'
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {products.map((product) => (
                <Option key={product._id} value={product._id}>
                  {product.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name='serviceFilters.categories' label='針對特定服務類別'>
            <Select mode='multiple' placeholder='請選擇服務類別' allowClear>
              {categories.map((category) => (
                <Option key={category} value={category}>
                  {category}
                </Option>
              ))}
            </Select>
          </Form.Item>
          </>
          )}

          {/* 通知內容 */}
          <Divider orientation='left'>通知內容</Divider>
          <Form.Item
            name='message.title'
            label='通知標題'
            rules={[{ required: true, message: '請輸入通知標題' }]}
            extra={
              <div>
                <div style={{ marginBottom: '8px' }}>支援變數：</div>
                <Space wrap>
                  <VariableButton
                    size='small'
                    onClick={() =>
                      insertVariable('message.title', '{customerName}')
                    }
                  >
                    {variableLabels['{customerName}']}
                  </VariableButton>
                  <VariableButton
                    size='small'
                    onClick={() =>
                      insertVariable('message.title', '{businessName}')
                    }
                  >
                    {variableLabels['{businessName}']}
                  </VariableButton>
                </Space>
              </div>
            }
          >
            <Input placeholder='例如: {customerName}，{businessName} 誠摯邀請您回訪！' />
          </Form.Item>

          <Form.Item
            name='message.content'
            label='通知內容'
            rules={[{ required: true, message: '請輸入通知內容' }]}
            extra={
              <div>
                <div style={{ marginBottom: '8px' }}>支援變數：</div>
                <Space wrap>
                  <VariableButton
                    size='small'
                    onClick={() =>
                      insertVariable('message.content', '{customerName}')
                    }
                  >
                    {variableLabels['{customerName}']}
                  </VariableButton>
                  <VariableButton
                    size='small'
                    onClick={() =>
                      insertVariable('message.content', '{businessName}')
                    }
                  >
                    {variableLabels['{businessName}']}
                  </VariableButton>
                  <VariableButton
                    size='small'
                    onClick={() =>
                      insertVariable('message.content', '{serviceName}')
                    }
                  >
                    {variableLabels['{serviceName}']}
                  </VariableButton>
                  <VariableButton
                    size='small'
                    onClick={() =>
                      insertVariable('message.content', '{lastVisitDate}')
                    }
                  >
                    {variableLabels['{lastVisitDate}']}
                  </VariableButton>
                </Space>
              </div>
            }
          >
            <TextArea
              rows={4}
              placeholder='例如: 親愛的 {customerName}，距離您上次來店（{lastVisitDate}）已經一段時間了，我們期待再次為您服務！'
            />
          </Form.Item>

          <Form.Item
            name='message.lineMessage'
            label='LINE 訊息內容（可選）'
            extra={
              <div>
                <div style={{ marginBottom: '8px' }}>支援變數：</div>
                <Space wrap>
                  <VariableButton
                    size='small'
                    onClick={() =>
                      insertVariable('message.lineMessage', '{customerName}')
                    }
                  >
                    {variableLabels['{customerName}']}
                  </VariableButton>
                  <VariableButton
                    size='small'
                    onClick={() =>
                      insertVariable('message.lineMessage', '{businessName}')
                    }
                  >
                    {variableLabels['{businessName}']}
                  </VariableButton>
                  <VariableButton
                    size='small'
                    onClick={() =>
                      insertVariable('message.lineMessage', '{serviceName}')
                    }
                  >
                    {variableLabels['{serviceName}']}
                  </VariableButton>
                  <VariableButton
                    size='small'
                    onClick={() =>
                      insertVariable('message.lineMessage', '{lastVisitDate}')
                    }
                  >
                    {variableLabels['{lastVisitDate}']}
                  </VariableButton>
                </Space>
                <div style={{ marginTop: '8px', color: '#999' }}>
                  如果不提供，則使用「通知內容」
                </div>
              </div>
            }
          >
            <TextArea rows={3} placeholder='LINE 專用訊息內容' />
          </Form.Item>

          <Form.Item
            name='message.emailContent'
            label='Email 內容（可選）'
            extra={
              <div>
                <div style={{ marginBottom: '8px' }}>支援變數：</div>
                <Space wrap>
                  <VariableButton
                    size='small'
                    onClick={() =>
                      insertVariable('message.emailContent', '{customerName}')
                    }
                  >
                    {variableLabels['{customerName}']}
                  </VariableButton>
                  <VariableButton
                    size='small'
                    onClick={() =>
                      insertVariable('message.emailContent', '{businessName}')
                    }
                  >
                    {variableLabels['{businessName}']}
                  </VariableButton>
                  <VariableButton
                    size='small'
                    onClick={() =>
                      insertVariable('message.emailContent', '{serviceName}')
                    }
                  >
                    {variableLabels['{serviceName}']}
                  </VariableButton>
                  <VariableButton
                    size='small'
                    onClick={() =>
                      insertVariable('message.emailContent', '{lastVisitDate}')
                    }
                  >
                    {variableLabels['{lastVisitDate}']}
                  </VariableButton>
                </Space>
                <div style={{ marginTop: '8px', color: '#999' }}>
                  如果不提供，則使用「通知內容」
                </div>
              </div>
            }
          >
            <TextArea rows={3} placeholder='Email 專用內容' />
          </Form.Item>

          <Form.Item
            name='message.showBookingButton'
            label='訊息底下顯示行動按鈕'
            valuePropName='checked'
            extra='關掉之後訊息底下就沒有按鈕。像「請補寄件資料」「公告」這類通知，留著「立即預約」會讓客人以為要先預約才能處理。'
          >
            <Switch checkedChildren='顯示' unCheckedChildren='不顯示' />
          </Form.Item>

          {showBookingButton && (
            <Form.Item
              name='message.buttonLabel'
              label='按鈕文字（可選）'
              extra='留空 = 「立即預約」。按鈕連結一律指向店家頁面。'
            >
              <Input placeholder='立即預約' maxLength={20} />
            </Form.Item>
          )}

          <Form.Item
            label='附圖（可選）'
            extra={
              <div style={{ color: '#999' }}>
                適合放狀況示意圖（例如術後可能出現的樣子），讓客人一眼看懂。
                <br />
                LINE 會在文字訊息之後，另外送出一則獨立的圖片訊息（客人可點開看原圖），
                因此<strong>一位客人會消耗 2 則推播額度</strong>；不放圖就只扣 1 則。
                Email 則直接顯示在內文中。
              </div>
            }
          >
            {reminderImage.imageUrl ? (
              <Space direction='vertical' size={8} style={{ width: '100%' }}>
                <img
                  src={resolveImageUrl(reminderImage.imageUrl)}
                  alt='提醒附圖'
                  style={{
                    maxWidth: '100%',
                    maxHeight: 220,
                    borderRadius: 8,
                    display: 'block'
                  }}
                />
                <Space>
                  <Upload
                    accept='image/*'
                    showUploadList={false}
                    beforeUpload={handleImageUpload}
                  >
                    <Button icon={<UploadOutlined />} loading={uploadingImage}>
                      更換圖片
                    </Button>
                  </Upload>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      setReminderImage({ imageUrl: '', imagePreviewUrl: '' })
                    }
                  >
                    移除
                  </Button>
                </Space>
              </Space>
            ) : (
              <Upload
                accept='image/*'
                showUploadList={false}
                beforeUpload={handleImageUpload}
              >
                <Button icon={<UploadOutlined />} loading={uploadingImage}>
                  上傳圖片
                </Button>
              </Upload>
            )}
          </Form.Item>

          {/* 發送設定 */}
          <Divider orientation='left'>發送設定</Divider>
          <Form.Item
            name='sending.batchSize'
            label='批次大小'
            rules={[
              { required: true, message: '請輸入批次大小' },
              {
                type: 'number',
                min: 1,
                max: 100,
                message: '批次大小必須在 1-100 之間'
              }
            ]}
          >
            <InputNumber
              min={1}
              max={100}
              placeholder='每次發送的最大收件人數量'
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name='sending.priority'
            label='發送方式優先順序'
            rules={[{ required: true, message: '請選擇發送方式' }]}
          >
            <Select mode='multiple' placeholder='請選擇發送方式'>
              <Option value='line'>LINE</Option>
              <Option value='email'>Email</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button onClick={() => navigate('/customers/revisit-reminders')}>
                取消
              </Button>
              <Button type='primary' htmlType='submit' loading={loading}>
                {isEditMode ? '更新' : '創建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </FormCard>
    </PageContainer>
  )
}

export default RevisitReminderConfigPage
